import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { useAppTheme } from '../../contexts/ThemeContext';
import { getVisibleSections } from './Sidebar.js';

const getStyles = (theme, sidebarMinimized) => ({
  layout: {
    display: 'flex',
    height: '100vh',
    backgroundColor: theme.colors.background.main,
    color: theme.colors.text.primary,
    fontFamily: theme.fontFamily.primary,
  },
  iconMenu: {
    backgroundColor: theme.colors.background.iconMenu,
    display: 'flex',
    flexDirection: 'column',
    width: theme.dimensions.iconMenuWidth,
    flexShrink: 0,
  },
  mainContentLeft: {
    boxShadow: sidebarMinimized ? 'none' : theme.shadows.leftLayout,
    backgroundColor: theme.colors.background.sidebar,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 20,
    width: sidebarMinimized
      ? theme.dimensions.iconMenuWidth
      : `calc(${theme.dimensions.iconMenuWidth} + ${theme.dimensions.sidebarWidth})`,
    minWidth: sidebarMinimized
      ? theme.dimensions.iconMenuWidth
      : `calc(${theme.dimensions.iconMenuWidth} + ${theme.dimensions.sidebarWidth})`,
    transition: 'width 0.3s ease, min-width 0.3s ease, box-shadow 0.3s ease',
    overflow: 'hidden',
  },
  mainContentLeftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: sidebarMinimized ? '10px' : '10px',
    background: theme.colors.background.sidebar,
    transition: 'padding 0.3s ease',
  },
  mainContentLeftWrapper: {
    display: 'flex',
    minHeight: '100px',
    flexGrow: 1,
  },
  sidebar: {
    width: sidebarMinimized ? '0px' : theme.dimensions.sidebarWidth,
    opacity: sidebarMinimized ? 0 : 1,
    overflow: 'hidden',
    backgroundColor: theme.colors.background.sidebar,
    display: 'flex',
    flexDirection: 'column',
    zIndex: theme.zIndex.sidebar,
    transition: 'width 0.3s ease, opacity 0.3s ease',
    pointerEvents: sidebarMinimized ? 'none' : 'auto',
  },
  mainContent: {
    flex: 1,
    backgroundColor: theme.colors.background.main,
    display: 'flex',
    flexDirection: 'column',
  },
  mainContentWrapper: {
    display: 'flex',
    minHeight: '100px',
    flexGrow: 1,
    position: 'relative',
  },
  logoContainer: {
    position: 'relative',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  hamburgerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.2s ease',
  },
  headerLogo: {
    width: theme.dimensions.headerLogoWidth,
    height: 'auto',
    transition: 'opacity 0.2s ease',
  },
  headerTitle: {
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.normal,
    color: theme.colors.text.primary,
    display: 'inline',
    opacity: sidebarMinimized ? 0 : 1,
    maxWidth: sidebarMinimized ? '0px' : '200px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    paddingLeft: '12px',
    transition: 'opacity 0.3s ease, max-width 0.3s ease',
  },
  mainContentBody: {
    flex: 1,
    overflow: 'auto',
    transition: theme.transitions.slow,
  },
  contextPanel: {
    backgroundColor: theme.colors.background.sidebar,
    display: 'flex',
    flexDirection: 'column',
    transition: theme.transitions.slow,
    position: 'relative',
    overflow: 'hidden',
  },
  contextPanelMinimized: {
    width: theme.dimensions.contextPanelMinimizedWidth,
    backgroundColor: theme.colors.background.sidebar,
    display: 'flex',
    flexDirection: 'column',
    transition: theme.transitions.slow,
    position: 'relative',
    overflow: 'hidden',
  },
  contextPanelNormal: {
    width: theme.dimensions.contextPanelNormalWidth,
  },
  contextPanelMaximized: {
    width: '50%',
    minWidth: '500px',
  },
  contextPanelToggle: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '30px',
    height: '30px',
    backgroundColor: theme.colors.background.sidebar,
    border: 'none',
    borderRadius: theme.borderRadius.sm,
    color: theme.colors.text.primary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.fontSize.xl,
    zIndex: 10,
    transition: theme.transitions.fast,
  },
  contextPanelContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  contextPanelMinimizedContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '50px',
    color: theme.colors.text.secondary,
    fontSize: theme.fontSize.xs,
  },
  contextPanelMinimizedIcon: {
    fontSize: theme.fontSize.xxxl,
    marginBottom: theme.spacing.sm,
  },
});

const Layout = ({ iconMenu, sidebar, mainContent, user }) => {
  const { theme, isDark } = useAppTheme();
  const location = useLocation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);

  const sidebarHasContent = useMemo(
    () => getVisibleSections(location.pathname, user).length > 0,
    [location.pathname, user],
  );
  const sidebarMinimized = isMinimized || !sidebarHasContent;
  const canToggle = sidebarHasContent;

  const styles = useMemo(() => getStyles(theme, sidebarMinimized), [theme, sidebarMinimized]);

  const logoSrc = isDark
    ? '/interface/images/verve_logos/small/VERVE-Symbol-Small-Scale-White.svg'
    : '/interface/images/verve_logos/small/VERVE-Symbol-Small-Scale-Black.svg';

  const handleLogoClick = () => {
    if (canToggle) {
      setIsMinimized((prev) => !prev);
    }
  };

  const showHamburger = canToggle && isLogoHovered;

  return (
    <div style={styles.layout}>
      <div style={styles.mainContentLeft}>
        <div style={styles.mainContentLeftHeader}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                ...styles.logoContainer,
                cursor: canToggle ? 'pointer' : 'default',
              }}
              onClick={handleLogoClick}
              onMouseEnter={() => setIsLogoHovered(true)}
              onMouseLeave={() => setIsLogoHovered(false)}
            >
              <img
                src={logoSrc}
                alt="Logo"
                style={{
                  ...styles.headerLogo,
                  opacity: showHamburger ? 0.15 : 1,
                }}
              />
              {canToggle && (
                <div
                  style={{
                    ...styles.hamburgerOverlay,
                    opacity: showHamburger ? 1 : 0,
                  }}
                >
                  <MenuIcon style={{ color: theme.colors.text.primary, fontSize: '28px' }} />
                </div>
              )}
            </div>
            <h1 style={styles.headerTitle}>Agent Studio</h1>
          </div>
        </div>
        <div style={styles.mainContentLeftWrapper}>
          <div style={styles.iconMenu}>{iconMenu}</div>
          <div style={styles.sidebar}>{sidebar}</div>
        </div>
      </div>

      <div style={styles.mainContent}>
        <div style={styles.mainContentBody}>{mainContent}</div>
      </div>
    </div>
  );
};

export default Layout;
